'use client';

import classNames from 'classnames';
import {
    CLPPurchaseOption,
    useClpPurchaseContext,
} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {publishEvent} from '@udemy-v2/event-tracking';
import styles from './subscription-purchase-content.module.scss';
import {useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {useClpNoticeContext} from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import {
    aggregateCollectionStats,
    FALLBACK_COLLECTION_STATS,
    PlanPeriodStyle,
    SubscribeFromPageCookie,
    SubscriptionCTA,
    SubscriptionLearnMoreEvent,
} from '@udemy-v2/react-subscription-browse';
import {SubscriptionPlanProductType} from '@udemy-v2/subscription-graphql';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useDeviceType} from '@udemy-v2/hooks';

const UI_REGION = 'purchase_section_tab0';
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];

export const SubscriptionPurchaseContent = () => {
    const {gettext, interpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const deviceType = useDeviceType();
    const isMobile = deviceType === 'mobile';

    const {
        isLoading,
        unwrappedConsumerSubscriptionPlan,
        selectedPurchaseOption,
        subscriptionCTAConfiguration,
    } = useClpPurchaseContext();
    const isValuePlanCourse =
        unwrappedConsumerSubscriptionPlan?.productType ===
        SubscriptionPlanProductType.ConsumersubscriptionValuePlan;

    const clpNoticeContext = useClpNoticeContext();
    const course = useClpCourse();
    const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;

    const defaultTitle = isValuePlanCourse
        ? gettext('Stay sharp (and curious)')
        : gettext('Subscribe to Udemy’s top courses');
    const title = subscriptionPriceBoxNotice?.title ?? defaultTitle;

    const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
    const subscriptionPlanStats = contentCollections
        ? aggregateCollectionStats(
              contentCollections.map((contentCollection) => contentCollection.stats),
          )
        : FALLBACK_COLLECTION_STATS;

    const defaultSubtitle = isValuePlanCourse
        ? interpolate(
              gettext('Get this course, plus %(courses_count)s+ of our top-rated courses.'),
              {courses_count: formatNumber(subscriptionPlanStats.numberOfCourses)},
              true,
          )
        : interpolate(
              gettext(
                  'Get this course, plus %(courses_count)s+ of our top-rated courses, with Personal Plan.',
              ),
              {courses_count: formatNumber(subscriptionPlanStats.numberOfCourses)},
              true,
          );
    const description = subscriptionPriceBoxNotice?.subtitle ?? defaultSubtitle;

    const learnMoreText = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext('Learn more');
    const subscriptionContextLearnMoreUrl =
        unwrappedConsumerSubscriptionPlan?.urlLearnMore ?? 'https://udemy.com/personal-plan/';
    const learnMoreUrl =
        subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
    const learnMoreTarget =
        subscriptionPriceBoxNotice?.subtitle_link_target === 'new_tab' ? '_blank' : '_self';

    const handleLearnMoreClick = () => {
        // When navigating to Personal Plan info / plan selection pages from CLP, set the
        // subscribe_from_page cookie so subscription checkout can attribute to this course.
        if (SUBSCRIPTION_ATTRIBUTION_PATHS.some((path) => learnMoreUrl.includes(path))) {
            SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
        }

        if (unwrappedConsumerSubscriptionPlan) {
            publishEvent(
                new SubscriptionLearnMoreEvent({
                    planId: unwrappedConsumerSubscriptionPlan.id,
                    planType: unwrappedConsumerSubscriptionPlan.productType,
                    uiRegion: UI_REGION,
                    planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds,
                }),
            );
        }
    };

    const handleCtaClick = () => {
        SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
    };

    if (!unwrappedConsumerSubscriptionPlan) {
        return null;
    }

    return (
        <div data-purpose="subscription-purchase-section">
            <div>
                <div className={classNames(styles.title, 'ud-heading-serif-xxl')}>{title}</div>
                <div className={classNames(styles.description, 'ud-text-sm')}>
                    {description}
                    {!isValuePlanCourse && (
                        <>
                            {' '}
                            <a
                                className={styles['learn-more-link']}
                                data-purpose="subscription-learn-more-link"
                                href={learnMoreUrl}
                                target={learnMoreTarget}
                                onClick={handleLearnMoreClick}
                            >
                                {learnMoreText}
                            </a>
                        </>
                    )}
                </div>
            </div>
            <SubscriptionCTA
                className={styles['subscription-cta']}
                uiRegion={UI_REGION}
                subtitleClassName={styles['cta-subtitle']}
                data-purpose="subscription-purchase-button"
                offerCtaStyle={
                    CLPPurchaseOption.SUBSCRIPTION === selectedPurchaseOption
                        ? 'primary'
                        : 'secondary'
                }
                onCTAClick={handleCtaClick}
                courseId={parseInt(course.id, 10)}
                includeTrialDaysOnCta={false}
                plan={unwrappedConsumerSubscriptionPlan}
                noticeData={subscriptionPriceBoxNotice}
                noticeDataLoading={isLoading}
                directCheckout={subscriptionCTAConfiguration?.directCheckout}
                selectPlanText={subscriptionCTAConfiguration?.selectPlanText}
                purchaseCtaText={subscriptionCTAConfiguration?.purchaseCtaText}
                planPeriodStyle={
                    isMobile
                        ? PlanPeriodStyle.MULTILINE_NO_MARGIN
                        : PlanPeriodStyle.MULTILINE_MARGIN
                }
            />
        </div>
    );
};
